import React, { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Animated, Platform, StyleSheet, TextInput, TextInputProps } from 'react-native';
import styled, { useTheme } from 'styled-components/native';
import { TextStyle } from 'react-native/Libraries/StyleSheet/StyleSheetTypes';
import { Box, BoxProps } from './box';
import { AppTextProps, AppTextView } from './app.text.view';

interface TextFieldProps extends Omit<TextInputProps, 'style' | 'onFocus' | 'ref'> {
  error?: boolean;
  helperText?: string | string[];
  endChild?: ReactElement | null;
  startChild?: ReactElement;
  underline?: boolean;
  fullWidth?: boolean;
  rounded?: boolean;
  border?: boolean;
  label?: string;
  textStyle?: TextStyle;
  autoGrow?: boolean;
  onFocus?: (focused: boolean) => void;
  floatingLabel?: boolean;
  floatingTextStyle?: AppTextProps;
  rows?: number;
  style?: BoxProps['style'];
}

export const BaseTextField = React.memo(
  React.forwardRef<TextInput, TextFieldProps>(
    (
      {
        floatingTextStyle,
        label,
        underline = true,
        textStyle,
        startChild,
        endChild,
        helperText,
        style,
        onFocus,
        value,
        autoGrow = false,
        error = false,
        border = false,
        fullWidth = false,
        rounded = false,
        multiline,
        floatingLabel = true,
        rows: _rows,
        onBlur: nativeOnBlur,
        onChange,
        ...rest
      }: TextFieldProps,
      ref: React.ForwardedRef<TextInput>,
    ) => {
      const theme = useTheme();
      const [focused, setFocused] = useState(false);
      const [contentSize, setContentSize] = useState<number | undefined>();
      const labelAnim = useRef(new Animated.Value(0)).current;
      const underlineAnim = useRef(new Animated.Value(0)).current;

      const floatingLabelText = label || (typeof rest.placeholder === 'string' ? rest.placeholder : undefined);
      const hasValue = !!value;
      const shouldUseFloatingLabel = !!floatingLabel && !!floatingLabelText;

      useEffect(() => {
        autoGrow && value === '' && setContentSize(undefined);
      }, [autoGrow, value]);

      useEffect(() => {
        Animated.timing(labelAnim, {
          toValue: focused || hasValue ? 1 : 0,
          duration: 160,
          useNativeDriver: true, // 🚀 Now running on the native UI thread
        }).start();
      }, [focused, hasValue, labelAnim]);

      useEffect(() => {
        if (underline) {
          Animated.timing(underlineAnim, {
            toValue: focused ? 1 : 0,
            duration: 220,
            useNativeDriver: true, // 🚀 scaleX is also supported by the native driver
          }).start();
        } else {
          underlineAnim.setValue(0);
        }
      }, [focused, underline, underlineAnim]);

      const onContentSizeChanged = useCallback(
        (e: any) => {
          if (autoGrow && e.nativeEvent?.contentSize?.height) {
            setContentSize(e.nativeEvent.contentSize.height);
          }
        },
        [autoGrow],
      );

      const handleFocus = useCallback(() => {
        setFocused(true);
        onFocus?.(true);
      }, [onFocus]);

      const handleBlur = useCallback(() => {
        setFocused(false);
        onFocus?.(false);
        nativeOnBlur?.(undefined);
      }, [onFocus, nativeOnBlur]);

      // Using translateY instead of 'top'
      const labelTranslateY = labelAnim.interpolate({
        inputRange: [0, 1],
        outputRange: [0, -22], // Glides up by 22 pixels
      });

      // Using scale instead of 'fontSize'
      const labelScale = labelAnim.interpolate({
        inputRange: [0, 1],
        outputRange: [1, 0.85], // Shrinks text to 85%
      });

      const underlineScaleX = underlineAnim.interpolate({
        inputRange: [0, 1],
        outputRange: [0, 1],
      });

      const inputDynamicStyle = useMemo(() => {
        // Check if we have hit the 150px limit
        const isMaxedOut = autoGrow && contentSize && contentSize >= 150;

        return {
          ...(textStyle || {}),
          ...(autoGrow && contentSize ? { height: Math.min(contentSize, 150), minHeight: 35, maxHeight: 150 } : { minHeight: 35 }),
          ...(Platform.OS === 'web' && isMaxedOut
            ? ({
                overflowY: 'auto',
                overscrollBehavior: 'contain',
              } as any)
            : {}),
        };
      }, [textStyle, autoGrow, contentSize]);

      const floatingLabelStyle = useMemo(
        () => ({
          // Static base values for the native driver to transform from
          top: 16,
          fontSize: theme.fontSizes.textField.primary,
          transform: [{ translateY: labelTranslateY }, { scale: labelScale }],
          ...(floatingTextStyle as any),
        }),
        [labelTranslateY, labelScale, theme.fontSizes.textField.primary, floatingTextStyle],
      );

      const underlineBarStyle = useMemo(
        () => ({
          backgroundColor: error ? theme.colors.textField.border.error : theme.colors.textField.border.primary,
          transform: [{ scaleX: underlineScaleX }],
        }),
        [error, theme, underlineScaleX],
      );

      const respondHandler = useCallback(
        (e) => {
          // Only claim the touch if the input has grown to its maximum height
          if (contentSize && contentSize >= 150) {
            e.stopPropagation();
            return true;
          }
          return false;
        },
        [contentSize],
      );

      const handleChange = useCallback(
        (e: any) => {
          if (Platform.OS === 'web' && autoGrow) {
            const target = e.nativeEvent?.target || e.target;

            if (target && typeof target.scrollHeight === 'number') {
              // 1. Save the current clamped height so the UI doesn't jump
              const previousHeight = target.style.height;

              // 2. Force the element to 0px. This forces the browser's
              // 'scrollHeight' to perfectly shrink-wrap the text.
              target.style.height = '0px';

              // 3. Synchronously read the true, smaller height of the text
              const trueHeight = target.scrollHeight;

              // 4. Instantly put the old height back before the browser paints the screen
              target.style.height = previousHeight;

              // 5. Update our React state directly with the accurate measurement
              setContentSize(trueHeight);
            }
          }

          // Forward the event if the parent component provided an onChange
          onChange?.(e);
        },
        [autoGrow, onChange],
      );

      return (
        <TextFieldWrapper $border={border} $rounded={rounded} $fullWidth={fullWidth} style={style}>
          <InputRow $floating={shouldUseFloatingLabel} $underline={underline}>
            {shouldUseFloatingLabel && (
              <FloatingLabelText pointerEvents={'none'} $error={error} style={floatingLabelStyle}>
                {floatingLabelText}
              </FloatingLabelText>
            )}
            {startChild && <StartChildWrapper>{startChild}</StartChildWrapper>}
            <Input
              autoComplete={'off'}
              importantForAccessibility={'no'}
              {...rest}
              onChange={handleChange}
              autoCorrect={false}
              spellCheck={false}
              ref={ref as any}
              multiline={multiline}
              onStartShouldSetResponder={respondHandler}
              value={value ?? ''}
              placeholder={shouldUseFloatingLabel ? '' : rest.placeholder}
              placeholderTextColor={theme.colors.textField.label}
              $error={error}
              onBlur={handleBlur}
              onFocus={handleFocus}
              onContentSizeChange={onContentSizeChanged}
              style={inputDynamicStyle as any}
              textAlignVertical={multiline ? 'top' : 'center'}
              autoCapitalize="none"
            />
            {endChild && <EndChildWrapper>{endChild}</EndChildWrapper>}
            {underline && <UnderlineBar style={underlineBarStyle} />}
          </InputRow>

          {!!helperText?.length && (
            <HelperRow>
              <HelperList>
                {Array.isArray(helperText) ? (
                  helperText.map((message) => (
                    <HelperText key={message} type={error ? 'error' : 'success'}>
                      {message}
                    </HelperText>
                  ))
                ) : (
                  <HelperText type={error ? 'error' : 'success'}>{helperText}</HelperText>
                )}
              </HelperList>
            </HelperRow>
          )}
        </TextFieldWrapper>
      );
    },
  ),
);

const TextFieldWrapper = styled(Box)<{ $border: boolean; $rounded: boolean; $fullWidth: boolean }>`
  ${({ $fullWidth }) => $fullWidth && 'width: 100%;'}
  border-radius: ${({ $rounded }) => ($rounded ? 30 : 8)}px;
  justify-content: center;
  border-color: ${({ $border, theme }) => ($border ? theme.colors.textField.border.primary : 'transparent')};
  border-width: ${({ $border }) => ($border ? 1 : 0)}px;
  padding: 5px;
`;

const InputRow = styled.View<{ $floating: boolean; $underline: boolean }>`
  flex-direction: row;
  align-items: center;
  position: relative;
  padding-top: ${({ $floating }) => ($floating ? 8 : 0)}px;
  border-bottom-width: ${({ $underline }) => ($underline ? StyleSheet.hairlineWidth : 0)}px;
  border-bottom-color: ${({ theme }) => theme.colors.textField.search_border};
`;

const UnderlineBar = styled(Animated.View)`
  position: absolute;
  left: 0;
  right: 0;
  bottom: -1px;
  height: 2px;
`;

// Note: Origin is top-right so the scale-down transformation naturally keeps it pinned to the right
const FloatingLabelText = styled(Animated.Text)<{ $error: boolean }>`
  position: absolute;
  right: 7px;
  z-index: 2;
  font-family: ${({ theme }) => theme.fonts.primary};
  color: ${({ $error, theme }) => ($error ? theme.colors.textField.error : theme.colors.textField.label)};
  text-align: right;
  background: transparent;
  padding: 0;
  transform-origin: top right;
`;

const Input = styled.TextInput<{ $error: boolean; $textAlign: string }>`
  flex: 1;
  font-size: ${({ theme }) => theme.fontSizes.textField.primary}px;
  font-family: ${({ theme }) => theme.fonts.primary};
  color: ${({ $error, theme }) => ($error ? theme.colors.textField.error : theme.colors.textField.primary)};
  border: none;
  padding: 7px;
  ${Platform.OS === 'android' && `direction: rtl;`}
  writing-direction: rtl; /* 👈 Forces the empty caret to start on the right on iOS */
  text-align: auto;
`;

const HelperText = styled(AppTextView)<{ type: 'error' | 'success' }>`
  color: ${({ type, theme }) => theme.colors.textField[type]};
`;

const EndChildWrapper = styled.View`
  justify-content: center;
  margin-left: 7px;
`;

const StartChildWrapper = styled.View`
  justify-content: center;
  margin-right: 7px;
`;

const HelperRow = styled.View`
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  margin-top: 5px;
`;

const HelperList = styled.View`
  flex-direction: column;
`;

export const AppTextField = React.memo(BaseTextField);
